import { Module } from 'vuex'
import { MenuItem } from '@/components/home/MenuCard.vue'

export interface MenuState {
  items: MenuItem[]
}

const DEFAULT_MENU: MenuItem[] = [
  { id: 1, name: 'Isombe with Ugali', description: 'Cassava leaves slow-cooked with eggplant, served with ugali.', price: 2500, category: 'Stews', emoji: '🫕' },
  { id: 2, name: 'Grilled Chicken & Rice', description: 'Juicy marinated chicken grilled to perfection with steamed rice.', price: 4000, category: 'Grills', emoji: '🍖' },
  { id: 3, name: 'Beans & Plantain', description: 'Rwandan-style red beans with fried sweet plantain.', price: 2000, category: 'Rice', emoji: '🍚' },
  { id: 4, name: 'Vegetable Soup', description: 'Light, nourishing soup with seasonal vegetables and herbs.', price: 1500, category: 'Soups', emoji: '🥣' },
  { id: 5, name: 'Mixed Salad', description: 'Fresh garden salad with avocado, tomatoes, and lemon dressing.', price: 1800, category: 'Salads', emoji: '🥗' },
  { id: 6, name: 'Passion Fruit Juice', description: 'Freshly squeezed passion fruit, chilled and lightly sweetened.', price: 800, category: 'Drinks', emoji: '🥤' },
  { id: 7, name: 'Beef Stew & Potatoes', description: 'Tender beef in rich tomato sauce with boiled potatoes.', price: 4500, category: 'Stews', emoji: '🫕' },
  { id: 8, name: 'Mandazi', description: 'Sweet, fluffy East African doughnuts — perfect with tea.', price: 500, category: 'Desserts', emoji: '🍮' },
  { id: 9, name: 'Fish & Chips', description: 'Crispy fried tilapia with spiced potato chips and salad.', price: 3500, category: 'Grills', emoji: '🐟' },
  { id: 10, name: 'Mashed Potatoes & Veggies', description: 'Creamy mashed potatoes with a mix of seasonal vegetables.', price: 2200, category: 'Rice', emoji: '🥔' },
  { id: 11, name: 'Pumpkin Soup', description: 'Velvety roasted pumpkin soup with a hint of ginger.', price: 1600, category: 'Soups', emoji: '🎃' },
  { id: 12, name: 'Avocado Smoothie', description: 'Thick, creamy avocado blended with milk and honey.', price: 1000, category: 'Drinks', emoji: '🥑' }
]

const STORAGE_KEY = 'hsb_menu'

function load (): MenuItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : DEFAULT_MENU
  } catch { return DEFAULT_MENU }
}

function save (items: MenuItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

const menu: Module<MenuState, unknown> = {
  namespaced: true,
  state: () => ({ items: load() }),
  getters: {
    all: (s) => s.items,
    available: (s) => s.items.filter(i => !i.soldOut),
    categories: (s) => ['All', ...new Set(s.items.map(i => i.category))]
  },
  mutations: {
    SET (state, items: MenuItem[]) {
      state.items = items
      save(items)
    },
    ADD (state, item: MenuItem) {
      const next = [...state.items, item]
      state.items = next
      save(next)
    },
    UPDATE (state, updated: MenuItem) {
      const idx = state.items.findIndex(i => i.id === updated.id)
      if (idx !== -1) {
        const next = [...state.items]
        next[idx] = updated
        state.items = next
        save(next)
      }
    },
    DELETE (state, id: number) {
      const next = state.items.filter(i => i.id !== id)
      state.items = next
      save(next)
    },
    TOGGLE_SOLD_OUT (state, id: number) {
      const idx = state.items.findIndex(i => i.id === id)
      if (idx !== -1) {
        const next = [...state.items]
        next[idx] = { ...next[idx], soldOut: !next[idx].soldOut }
        state.items = next
        save(next)
      }
    }
  },
  actions: {
    add ({ commit }, item: MenuItem) { commit('ADD', item) },
    update ({ commit }, item: MenuItem) { commit('UPDATE', item) },
    delete ({ commit }, id: number) { commit('DELETE', id) },
    toggleSoldOut ({ commit }, id: number) { commit('TOGGLE_SOLD_OUT', id) },
    reset ({ commit }) { commit('SET', DEFAULT_MENU) }
  }
}

export default menu
